import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Star, Globe } from 'lucide-react';
import { Button, Input, Select, Card, CardContent, Checkbox, cn } from '../../ui';
import {
  getCurso, getCategorias, getProveedores, createCurso, updateCurso, subirImagenCurso, imagenCursoUrl,
} from '../api';
import { handleAuthError } from '../../../../funciones/authHelper';
import useImagenBlob from '../../../../funciones/useImagenBlob';

const VACIO = {
  nombre: '', modalidad: 'curso', ref: '', horas: '', importe: '', proveedor_id: '',
  temario_url: '', objetivos: '',
  destacado: false, activo: true, publicado_web: true, en_catalogo: false,
};

function Bloque({ titulo, children }) {
  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">{titulo}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

export default function LaeCursoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = !!id;

  const [form, setForm] = useState(VACIO);
  const [cats, setCats] = useState([]);
  const [provs, setProvs] = useState([]);
  const [selCats, setSelCats] = useState([]);
  const [imagen, setImagen] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(editando);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Imagen ya guardada: el hook descarga el blob con Bearer y la sirve como
  // ObjectURL. La preview local de un archivo recién elegido tiene prioridad.
  const imagenUrl = useImagenBlob(imagen ? imagenCursoUrl(imagen) : null);

  useEffect(() => {
    getCategorias().then(({ data }) => setCats(data)).catch((e) => handleAuthError && handleAuthError(e));
    getProveedores().then(({ data }) => setProvs(data)).catch(() => { /* puede no tener permiso de proveedores */ });
  }, []);

  useEffect(() => {
    if (!editando) return;
    setLoading(true);
    getCurso(id).then(({ data }) => {
      setForm({
        nombre: data.nombre || '', modalidad: data.modalidad || 'curso',
        ref: data.ref ?? '', horas: data.horas ?? '', importe: data.importe ?? '',
        proveedor_id: data.proveedor_id ?? '',
        temario_url: data.temario_url || '', objetivos: data.objetivos || '',
        destacado: !!data.destacado, activo: !!data.activo,
        publicado_web: data.publicado_web === undefined ? true : !!data.publicado_web,
        en_catalogo: !!data.en_catalogo,
      });
      setSelCats((data.categorias || []).map((c) => c.id));
      setImagen(data.imagen || null);
    }).catch((e) => handleAuthError && handleAuthError(e))
      .finally(() => setLoading(false));
  }, [id, editando]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (cid) =>
    setSelCats((s) => (s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, categorias: selCats };
      let cursoId = id;
      if (editando) {
        await updateCurso(id, payload);
      } else {
        const { data } = await createCurso(payload);
        cursoId = data.id;
      }
      if (file && cursoId) await subirImagenCurso(cursoId, file);
      navigate('/formacion/lae/cursos');
    } catch (e) {
      setErr(e?.response?.data?.error || 'No se pudo guardar el curso.');
      if (handleAuthError) handleAuthError(e);
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="bg-slate-50 px-6 py-5 min-h-full text-slate-400">Cargando…</div>;
  }

  const imgActual = preview || imagenUrl;

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">

      <div className="mb-5 flex items-center gap-3">
        <button onClick={() => navigate('/formacion/lae/cursos')} className="text-slate-400 hover:text-slate-700">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">{editando ? 'Editar curso' : 'Nuevo curso'}</h1>
          <p className="text-sm text-slate-500 mt-0.5">LAE Formación</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/formacion/lae/cursos')}>Cancelar</Button>
        <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <Bloque titulo="Datos básicos">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <Input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} placeholder="Ej. Inglés para hostelería" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Modalidad</label>
                  <Select value={form.modalidad} onChange={(e) => set('modalidad', e.target.value)}>
                    <option value="curso">Curso</option>
                    <option value="videocurso">Videocurso</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ref.</label>
                  <Input type="number" value={form.ref} onChange={(e) => set('ref', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Horas</label>
                  <Input type="number" min={0} value={form.horas} onChange={(e) => set('horas', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Importe (€)</label>
                  <Input type="number" min={0} step="0.01" value={form.importe} onChange={(e) => set('importe', e.target.value)} />
                </div>
              </div>
              <div className="max-w-sm">
                <label className="block text-sm font-medium text-slate-700 mb-1">Proveedor</label>
                <Select value={form.proveedor_id} onChange={(e) => set('proveedor_id', e.target.value)}>
                  <option value="">— Sin proveedor —</option>
                  {provs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </Select>
              </div>
            </div>
          </Bloque>

          <Bloque titulo="Clasificación">
            <label className="block text-sm font-medium text-slate-700 mb-2">Categorías (puede estar en varias)</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {cats.length === 0 && <span className="text-sm text-slate-400">No hay categorías todavía.</span>}
              {cats.map((c) => {
                const on = selCats.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCat(c.id)}
                    className={cn(
                      'text-sm px-3 py-1.5 rounded-full border transition',
                      on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                    )}
                  >
                    {c.nombre}
                  </button>
                );
              })}
            </div>
          </Bloque>

          <Bloque titulo="Contenido">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Objetivos</label>
                <textarea
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm min-h-[120px]"
                  value={form.objetivos}
                  onChange={(e) => set('objetivos', e.target.value)}
                  placeholder="Objetivos del curso…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Temario (enlace)</label>
                <Input value={form.temario_url} onChange={(e) => set('temario_url', e.target.value)} placeholder="https://…" />
                <p className="text-xs text-slate-400 mt-1">El temario es un enlace (PDF/web).</p>
              </div>
            </div>
          </Bloque>
        </div>

        <div>
          <Bloque titulo="Imagen">
            <div className="aspect-video rounded-lg border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center mb-3">
              {imgActual ? <img src={imgActual} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-300 text-sm">Sin imagen</span>}
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
              <ImagePlus className="w-4 h-4" />
              {imgActual ? 'Cambiar imagen' : 'Subir imagen'}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
            <p className="text-xs text-slate-400 mt-2">Se sube a Azure (imagenesCursos/). Máx. 5 MB.</p>
          </Bloque>

          <Bloque titulo="Publicación">
            <div className="space-y-3">
              {/* Estado del curso (Activo / Inactivo): independiente de la
                  publicación. Si está inactivo, los demás flags se mantienen
                  pero no surten efecto en la web. */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Estado</label>
                <div className="inline-flex items-center bg-slate-100 border border-slate-200 rounded-md p-0.5">
                  <button
                    type="button"
                    onClick={() => set('activo', true)}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded transition-colors',
                      form.activo
                        ? 'bg-white text-emerald-700 shadow-sm font-medium'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                  >
                    Activo
                  </button>
                  <button
                    type="button"
                    onClick={() => set('activo', false)}
                    className={cn(
                      'px-3 py-1.5 text-sm rounded transition-colors',
                      !form.activo
                        ? 'bg-white text-slate-700 shadow-sm font-medium'
                        : 'text-slate-500 hover:text-slate-700'
                    )}
                  >
                    Inactivo
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-1.5">
                  {form.activo
                    ? 'Curso vivo: las marcas de abajo (publicado en web, catálogo) aplican.'
                    : 'Curso inactivo: aunque haya marcas, no aparecerá en la web ni en el catálogo.'}
                </p>
              </div>

              <div className={cn('space-y-2.5 pt-1', !form.activo && 'opacity-50 pointer-events-none')}>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <Checkbox checked={form.publicado_web} onChange={(e) => set('publicado_web', e.target.checked)} />
                  <span className="text-sm text-slate-700 flex items-center gap-1.5"><Globe className="w-4 h-4 text-emerald-600" /> Publicado en la web</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <Checkbox checked={form.destacado} onChange={(e) => set('destacado', e.target.checked)} />
                  <span className="text-sm text-slate-700 flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-500" /> Destacado (en la home)</span>
                </label>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <Checkbox checked={form.en_catalogo} onChange={(e) => set('en_catalogo', e.target.checked)} />
                  <span className="text-sm text-slate-700">Incluir en catálogo (PDF)</span>
                </label>
              </div>
            </div>
          </Bloque>
        </div>
      </div>

      {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}

      <div className="flex justify-end gap-2 mt-3">
        <Button variant="secondary" onClick={() => navigate('/formacion/lae/cursos')}>Cancelar</Button>
        <Button onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </div>
    </div>
  );
}
